import type { TicketTierDto } from '../api/tickets.api';

/**
 * Rangul scolii in editie — DERIVAT DIN `earned`, niciodata din `available`.
 *
 * 🔴 Cele doua cantitati NU se amesteca, si asta e intregul motiv pentru care modulul exista:
 *
 *   - `earned`    = totalul castigat in editie. Decide NIVELUL. Cheltuiala nu-l atinge.
 *   - `available` = soldul (`earned - spent`). Decide CE ISI PERMITE ACUM. Scade la rascumparare.
 *
 * Pana acum, bara de progres din `BalanceCard` era calculata din `available` (tinta venea din
 * `SchoolPointsPage`, filtrata pe starea `insufficient`, care depinde de sold). Consecinta:
 * o scoala care isi lua o recompensa vedea bara SCAZAND — adica ecranul ii spunea ca a fost
 * retrogradata pentru ca a cheltuit. Nivelul nu se pierde prin cheltuiala; doar asa arata.
 *
 * ⚠️ Ce NU are voie sa afirme nimic construit peste modulul asta: „rangul nu scade niciodata".
 * `earned` e o suma RECALCULATA la fiecare citire peste randurile existente (vezi
 * `UserRepository.findSumOfPointsBySchoolId`), iar respingerea unei activitati sterge randul —
 * deci `earned` POATE scadea, ca o corectie de date. Afirmatia adevarata, si singura permisa,
 * e mai ingusta: **cheltuiala nu coboara nivelul**. Aia e garantata mecanic: `TicketService.redeem`
 * scrie doar in `ticket_redemptions` si nu atinge niciun punct de utilizator.
 */

/**
 * Unde se afla scoala fata de un nivel. Trei stari, si sunt despre POZITIE.
 *
 * Nu se confunda cu cele patru stari de rascumparare din `tier-state.ts` (`available`,
 * `insufficient`, `outside-window`, `limit-reached`), care sunt despre CE POTI FACE ACUM si se
 * calculeaza din sold, fereastra si limita. Cele doua familii coexista pe acelasi nivel: poti fi
 * `atins` structural si `limit-reached` la rascumparare in acelasi timp.
 */
export type StareStructurala = 'atins' | 'curent' | 'neatins';

export interface NivelCuStare {
    tier: TicketTierDto;
    stare: StareStructurala;
    /** Pozitia in scara ordonata, de la 0. Decide banda de culoare. */
    index: number;
}

export interface RangEditie {
    /** Toate nivelurile, ordonate crescator. Se afiseaza toate, si cele neatinse. */
    niveluri: NivelCuStare[];
    /** Cel mai inalt nivel atins. `undefined` cand scoala n-a atins inca niciunul. */
    curent?: TicketTierDto;
    /** Primul nivel neatins. `undefined` doar cand scoala e pe ultimul. */
    urmator?: TicketTierDto;
    /** Puncte pana la `urmator`. `0` cand nu exista urmator (scoala e la varf). */
    distanta: number;
    /** `true` cand nu mai exista nimic de atins. Atunci eroul paginii devine soldul. */
    laVarf: boolean;
}

/** `pointsCost` e declarat obligatoriu in tip, dar tratat defensiv peste tot in domeniu. */
function cost(tier: TicketTierDto): number {
    return tier.pointsCost ?? 0;
}

/**
 * Ordonarea scarii se face dupa COST, nu dupa `level`.
 *
 * `level` e o eticheta pusa de admin in `/admin/niveluri-tichete`; costul e ce se compara cu
 * `earned`. Daca cele doua ajung sa se contrazica — nivelul 2 mai ieftin decat nivelul 1 —
 * ordonarea dupa cost pastreaza scara coerenta: „atins" ramane monoton crescator, deci nu poti
 * avea un nivel atins deasupra unuia neatins. Ordonarea dupa `level` ar produce exact asta.
 * `level` ramane departajator la cost egal, ca ordinea sa fie stabila intre randari.
 */
function ordoneaza(niveluri: TicketTierDto[]): TicketTierDto[] {
    return [...niveluri].sort((a, b) => cost(a) - cost(b) || (a.level ?? 0) - (b.level ?? 0));
}

/**
 * Starile structurale ale tuturor nivelurilor, plus distanta pana la urmatorul.
 *
 * Pragul e INCLUSIV (`earned >= cost`), ca in backend: `TicketService.redeem` refuza abia cand
 * `available < pointsCost`, deci exact pe prag rascumpararea e permisa. Un `>` aici ar arata
 * „neatins" fix pe cifra pe care serverul o accepta — genul de off-by-one care nu se vede.
 */
export function calculeazaRang(niveluri: TicketTierDto[], earned: number): RangEditie {
    const scara = ordoneaza(niveluri);
    const atinse = scara.filter((t) => earned >= cost(t));
    const curent = atinse.length > 0 ? atinse[atinse.length - 1] : undefined;
    const urmator = scara.find((t) => earned < cost(t));

    return {
        niveluri: scara.map((tier, index) => ({
            tier,
            index,
            stare: tier === curent ? 'curent' : earned >= cost(tier) ? 'atins' : 'neatins',
        })),
        curent,
        urmator,
        // Fara `urmator` nu exista distanta. `0`, nu `undefined`, ca apelantul sa nu trebuiasca
        // sa se pazeasca de doua ori: `laVarf` e semnalul, distanta e doar cifra.
        distanta: urmator ? Math.max(0, cost(urmator) - earned) : 0,
        laVarf: scara.length > 0 && urmator === undefined,
    };
}

/**
 * Verzii, de la deschis la inchis, plus ruperea in magenta de la varf.
 *
 * ⚠️ Patru din cinci sunt culorile CLIENTULUI (rang-1..4). `--color-rang-0` (#89D9A8) e
 * PRESUPUS de noi — propunere S1, masurata, acceptata de owner pe 2026-09-05 pentru
 * „Prieten Eco", pana vine culoarea clientului. Criteriul dupa care se inlocuieste, daca
 * vine alta: deltaE2000 >= 6,3 fata de #6FBE8F, L > 0,738, contrast cu #1B2B22 >= 4,5.
 *
 * ⚠️ `--color-rang-varf` (#D6249F) e magenta ADEVARAT. Tokenul `--color-magenta` din `main.css`
 * e, in ciuda numelui, VERDE (#0b632d) — culoarea de brand de pe site. Nu sunt acelasi lucru si
 * nu se substituie unul altuia.
 */
const VERZI = [
    '--color-rang-0',
    '--color-rang-1',
    '--color-rang-2',
    '--color-rang-3',
    '--color-rang-4',
] as const;
const VARF = '--color-rang-varf';

/**
 * Numele tokenului pentru banda unui nivel, dupa pozitia lui in scara.
 *
 * Regula: **ultimul nivel primeste intotdeauna magenta**. Ruperea de culoare de la varf e ce face
 * varful sa para varf; daca ar fi doar „inca un verde, mai inchis", ultimul nivel ar arata ca o
 * continuare, nu ca o destinatie.
 *
 * Restul iau verzii aliniati la CAPAT, nu la inceput: verdele cel mai inchis sta mereu lipit de
 * magenta, indiferent cate niveluri sunt. Cu exact cinci niveluri iese fix maparea data de client
 * (EXPLORATOR → CAMPION pe cei patru verzi, LEGENDĂ pe magenta).
 *
 * ✅ Limitarea de la SASE niveluri — cazul care vine, cand „Prieten Eco" devine rand real — e
 * INCHISA: pana la 2026-09-06 verzii nu ajungeau, si primele doua trepte cadeau pe acelasi verde
 * deschis (masurat de S1 la grila 22, nu dedus). S-a adaugat al cincilea verde, `--color-rang-0`.
 *
 * ⚠️ Alinierea la capat face ca schimbarea sa NU atinga niciun numar de trepte <= 5: cu cinci,
 * `pozitie` porneste de la 1 si iese aceeasi mapare ca inainte. Singurul comportament schimbat e
 * la >= 6 trepte. Testul cu sase trepte era, pana acum, specificatia bugului — asa cum au fost
 * cele 42 de teste de la redirectul pinuit pe home.
 *
 * La SAPTE trepte problema se intoarce, in acelasi loc: primele doua ar imparti `--color-rang-0`.
 * Nu se rezolva aici cu inca un token — sapte benzi verzi nu se mai disting oricum una de alta;
 * atunci se schimba mecanismul, nu paleta.
 */
export function tokenBanda(index: number, total: number): string {
    if (total <= 0) return VARF;
    if (index >= total - 1) return VARF;

    const cateVerzi = total - 1;
    // Aliniere la capat: cand sunt mai putine trepte decat verzi, se folosesc cei INCHISI.
    const pozitie = VERZI.length - cateVerzi + index;
    return VERZI[Math.min(VERZI.length - 1, Math.max(0, pozitie))];
}

/** Eticheta scrisa a starii — al doilea semn, obligatoriu langa culoare. */
export function etichetaStare(stare: StareStructurala): string {
    if (stare === 'curent') return 'Nivelul tău';
    if (stare === 'atins') return 'Atins';
    return 'Neatins';
}
